import React, { useState } from 'react';
import { apiClient } from '../../api/client';
import Text from '../Text/Text';
import { useUI, useAuth } from '../../hooks';
import './UserIdModal.css';

interface UserIdModalProps {
  isOpen: boolean;
  onClose?: () => void;
}

const UserIdModal: React.FC<UserIdModalProps> = ({ isOpen, onClose }) => {
  const { showToast } = useUI();
  const { refreshCurrentUser } = useAuth();
  const [userId, setUserId] = useState('');
  const [error, setError] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateUserId = (id: string) => {
    const regex = /^[a-zA-Z0-9_-]{5,50}$/;
    return regex.test(id);
  };

  const handleUserIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserId(e.target.value);
    setError('');
  };

  const checkUserIdUniqueness = async () => {
    if (!validateUserId(userId)) {
      setError('VeriPay ID must be 5-50 characters long and contain only letters, numbers, dashes, and underscores.');
      return false;
    }

    setIsValidating(true);
    try {
      await apiClient.users.validateUserId(userId);
      setError('User ID already exists. Please choose another one.');
      setIsValidating(false);
      return false;
    } catch (error: any) {
      if (error.response?.status === 404) {
        // 404 means the user ID doesn't exist, which is what we want
        setIsValidating(false);
        return true;
      }
      setError('Error validating User ID. Please try again.');
      setIsValidating(false);
      return false;
    }
  };

  const handleSubmit = async () => {
    if (!userId) {
      setError('VeriPay ID is required');
      return;
    }

    const isUnique = await checkUserIdUniqueness();
    if (!isUnique) {
      return;
    }

    setIsSubmitting(true);
    try {
      await apiClient.users.setUserId(userId);

      // Refresh the current user data to update the UI with the new VeriPay ID
      console.log('UserIdModal: Refreshing current user data after setting VeriPay ID');
      await refreshCurrentUser();
      
      setIsSubmitting(false);
      
      // Show success toast notification
      showToast('success', 'VeriPay ID successfully created');
      
      // Close the modal
      if (onClose) {
        onClose();
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Error setting VeriPay ID');
      setIsSubmitting(false);
    }
  };

  return isOpen ? (
    <div className="user-id-modal-overlay">
      <div className="user-id-modal">
        <div className="user-id-modal-header">
          <Text className="text-h2">Create Your VeriPay ID</Text>
        </div>
        <div className="user-id-modal-content">
          <Text className="text-body">
            A VeriPay ID is required to use VeriPay. This unique identifier will be used for all your transactions.
          </Text>
          <Text className="text-body">
            Choose a VeriPay ID that is easy to remember and share with others. It must be at least 5 characters long and can contain letters, numbers, dashes, and underscores.
          </Text>
          <div className="user-id-modal-form-group">
            <Text className="text-body">VeriPay ID</Text>
            <input
              type="text"
              className={`input-field ${error ? 'input-error' : ''}`}
              placeholder="Enter your VeriPay ID"
              value={userId}
              onChange={handleUserIdChange}
            />
            {error && (
              <Text className="text-caption text-error">
                {error}
              </Text>
            )}
          </div>
        </div>
        <div className="user-id-modal-footer">
          <button
            className="submit-button"
            onClick={handleSubmit}
            disabled={isValidating || isSubmitting}
          >
            <Text className="text-body text-white">
              {isSubmitting ? 'Creating...' : 'Create VeriPay ID'}
            </Text>
          </button>
        </div>
      </div>
    </div>
  ) : null;
};

export default UserIdModal;